import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { useShowErrorStore, useModalStore, useUserStore } from '~/stores'
import { MODAL_TYPE } from '~/constants'

export const useNotify = () => {
    const useShowErrorStoreInstance = useShowErrorStore()
    const { onShowErrorModal } = useShowErrorStoreInstance
    const route = useRoute()
    const brandName = useRuntimeConfig().public.BRAND_NAME
    const useModalStoreInstance = useModalStore()
    const {
        showLoginModal,
        showForgotPasswordModal,
        showAddBankModal,
        showSignUpModal,
    } = storeToRefs(useModalStoreInstance)
    const useUserStoreInstances = useUserStore()
    const { logout } = useUserStoreInstances

    const onClickLiveChat = () => {
        window.LiveChatWidget.call('maximize')
    }

    const t = (
        message = '',
        params: Record<string, unknown> | undefined = undefined
    ) => {
        if (params) {
            return {
                message,
                params,
            }
        }
        return message
    }

    const showGameDenyModal = () => {
        onShowErrorModal({
            title: t('error_modal-game_deny-title'),
            message: t('error_modal-game_deny-message'),
            cancelButton: t('error_modal-game_deny-cancel'),
            confirmButton: t('error_modal-game_deny-confirm'),
            icon: '/assets/img/v2/icons/ic-game-deny.png',
            onConfirm: () => {
                navigateTo('/account/general-promotions')
            },
        })
    }

    const showGameMaintenanceModal = (closeAction = () => {}) => {
        onShowErrorModal({
            title: t('error_modal-game_maintenance-title'),
            message: t('error_modal-game_maintenance-message'),
            cancelButton: t('error_modal-game_maintenance-cancel'),
            confirmButton: t('error_modal-game_maintenance-confirm'),
            icon: '/assets/img/v2/icons/ic-game-maintenance.png',
            onClose: () => {
                closeAction()
            },
            onCancel: () => {
                navigateTo('/')
            },
            onConfirm: () => {
                navigateTo('/games')
            },
        })
    }

    const showRegisterSuccessModal = () => {
        onShowErrorModal({
            title: t('error_modal-register-success-title'),
            message: `${t('error_modal-register-success-message')} ${brandName}`,
            cancelButton: t('error_modal-register-success-cancel'),
            confirmButton: t('error_modal-register-success-confirm'),
            icon: '/assets/images/modal/ic-register-success.svg',
            onCancel: () => {
                navigateTo('/')
            },
            onConfirm: () => {
                navigateTo('/events?tab=promotions')
            },
        })
    }

    const showRegisterFailedModal = (message = '') => {
        onShowErrorModal({
            title: t('error_modal-register-error-title'),
            message: message || t('error_modal-register-error-message'),
            cancelButton: t('error_modal-register-error-cancel'),
            confirmButton: t('error_modal-register-error-confirm'),
            icon: '/assets/images/modal/ic-register-error.svg',
            onClose: () => {},
            onCancel: () => {
                navigateTo('/')
            },
            onConfirm: () => {
                onClickLiveChat()
            },
        })
    }

    const showLoginFailedModal = (message = '') => {
        onShowErrorModal({
            modalName: 'login-failed-modal',
            title: t('error_modal-login-error-title'),
            message: message || t('error_modal-login-error-message'),
            confirmButton: t('modal-signup-verify'),
            icon: '/assets/img/v2/icons/error-login.png',
            onConfirm: () => {
                showLoginModal.value = true
            },
        })
    }

    const showLoggedExpiredModal = (message = '') => {
        onShowErrorModal({
            title: t('error_modal-login-logged_expired-title'),
            message: t('error_modal-login-logged_expired-message'),
            confirmButton: t('error_modal-login-logged_expired-confirm'),
            icon: '/assets/images/modal/ic-login-expired.svg',
            onConfirm: () => {
                showErrorModal.value = true
            },
        })
    }

    const showLogoutWarningModal = () => {
        onShowErrorModal({
            title: t('error_modal-logout-title'),
            message: t('error_modal-logout-message'),
            cancelButton: t('error_modal-logout-cancel'),
            confirmButton: t('error_modal-logout-confirm'),
            icon: '/assets/images/modal/ic-logout-warning.svg',
            onConfirm: () => {
                logout()
            },
        })
    }

    const showSendMailFailedModal = (message = '') => {
        onShowErrorModal({
            title: t('error_modal-sendmail-error-title'),
            message: message || t('error_modal-sendmail-error-message'),
            cancelButton: t('error_modal-sendmail-error-cancel'),
            confirmButton: t('error_modal-sendmail-error-confirm'),
            icon: '/assets/img/v2/icons/email-error.png',
            onCancel: () => {
                navigateTo('/')
            },
        })
    }

    const showSendMailSuccessModal = (message = '') => {
        onShowErrorModal({
            title: t('error_modal-sendmail-success-title'),
            message: message || t('error_modal-sendmail-success-message'),
            cancelButton: t('error_modal-sendmail-success-cancel'),
            confirmButton: t('error_modal-sendmail-success-confirm'),
            icon: '/assets/img/v2/icons/email-success.png',
            onCancel: () => {
                showForgotPasswordModal.value = false
                navigateTo('/')
            },
        })
    }

    const showSendTelegramSuccessModal = (message = '') => {
        onShowErrorModal({
            title: t('error_modal-sendtelegram-success-title'),
            message: message || t('error_modal-sendtelegram-success-message'),
            cancelButton: t('error_modal-sendtelegram-success-cancel'),
            confirmButton: t('error_modal-sendtelegram-success-confirm'),
            icon: '/assets/img/v2/icons/tele-success.png',
            onCancel: () => {
                navigateTo('/')
            },
        })
    }

    const showChangePasswordSuccessModal = () => {
        onShowErrorModal({
            title: t('error_modal-changepass-success-title'),
            message: t('error_modal-changepass-success-message', {
                tag: '<br />',
            }),
            cancelButton: t('error_modal-changepass-success-cancel'),
            confirmButton: t('error_modal-changepass-success-confirm'),
            icon: '/assets/img/v2/icons/success-password.png',
            onClose: () => {
                showForgotPasswordModal.value = false
                showLoginModal.value = true
            },
        })
    }
    const showChangePasswordFailedModal = (message = '') => {
        onShowErrorModal({
            title: t('error_modal-changepass-error-title'),
            message: message || t('error_modal-changepass-error-message'),
            cancelButton: t('error_modal-changepass-error-cancel'),
            confirmButton: t('error_modal-changepass-error-confirm'),
            icon: '/assets/img/v2/icons/error-password.png',
            onCancel: () => {
                navigateTo('/')
            },
        })
    }

    const showCreatePasswordSuccessModal = () => {
        onShowErrorModal({
            title: t('error_modal-changepass-create-title'),
            message: t('error_modal-changepass-create-message'),
            icon: '/assets/images/modal/ic-create-password-success.svg',
        })
    }

    const showMessage = (
        { message = '', type = MODAL_TYPE.ERROR, callback = () => {} },
        options = undefined
    ) => {
        onShowErrorModal({
            type,
            title:
                type === MODAL_TYPE.ERROR
                    ? t('error_modal-system-title')
                    : t('modal-common-success-title'),
            message:
                message ||
                (type === MODAL_TYPE.ERROR
                    ? t('error_modal-system-message')
                    : t('modal-common-success-message')),
            cancelButton:
                type.toLowerCase() === MODAL_TYPE.ERROR
                    ? t('error_modal-system-cancel')
                    : null,
            confirmButton:
                type.toLowerCase() === MODAL_TYPE.ERROR
                    ? t('error_modal-system-confirm_cskh')
                    : t('error_modal-system-confirm'),
            onCancel: () => {
                navigateTo('/')
                callback()

                if (options?.onCancel) {
                    options.onCancel()
                }
            },
            onConfirm: () => {
                if (type.toLowerCase() === MODAL_TYPE.ERROR) {
                    onClickLiveChat()
                }
                callback()

                if (options?.onConfirm) {
                    options.onConfirm()
                }
            },
        })
    }

    const showMessageError = (payload, option = undefined) => {
        const reg500 = '50[0-9]'
        const reg400 = '40[1-9]'
        const globalRegex500 = new RegExp(reg500, 'g')
        const globalRegex400 = new RegExp(reg400, 'g')
        let message = t('error_modal-system-message')
        if (globalRegex500.test(payload?.value?.statusCode)) {
            message = t('error-big_is_busy')
        } else if (globalRegex400.test(payload?.value?.statusCode)) {
            message = t('error-big_is_busy')
        } else if (payload?.value?.data?.data?.message) {
            message = payload?.value?.data?.data?.message
        } else if (payload?.value?.message) {
            message = payload?.value?.message
        } else if (payload.message) {
            message = payload.message
        } else if (payload && typeof payload === 'string') {
            message = payload
        }
        showMessage({ message, type: MODAL_TYPE.ERROR }, option)
    }

    const showMessageSuccess = (
        message = t('error_modal-system_success-message')
    ) => {
        showMessage({
            message,
            type: MODAL_TYPE.SUCCESS,
        })
    }

    const showBalanceFailedModal = () => {
        onShowErrorModal({
            title: t('error_modal-user-balance_failed-title'),
            message: `${t('error_modal-user-balance_failed-message')} ${brandName}`,
            cancelButton: t('error_modal-user-balance_failed-cancel'),
            confirmButton: t('error_modal-user-balance_failed-confirm'),
            icon: '/assets/images/modal/ic-balance-error.svg',
            onCancel: () => {
                navigateTo('/')
            },
            onConfirm: () => {
                navigateTo('/account/deposit')
            },
        })
    }

    const showDepositPlay = () => {
        onShowErrorModal({
            title: t('error_modal-user-deposit_play-title'),
            message: t('error_modal-user-deposit_play-message'),
            cancelButton: t('error_modal-user-deposit_play-cancel'),
            confirmButton: t('error_modal-user-deposit_play-confirm'),
            icon: '/assets/images/modal/ic-balance-error.svg',
            onCancel: () => {
                navigateTo('/')
            },
            onConfirm: () => {
                navigateTo('/account/deposit')
            },
        })
    }

    const showCancelPromotionSuccessModal = () => {
        onShowErrorModal({
            title: t('error_modal-user-balance_failed-title'),
            message: t('error_modal-user-balance_failed-message'),
            cancelButton: t('error_modal-user-balance_failed-cancel'),
            confirmButton: t('error_modal-user-balance_failed-confirm'),
            icon: '/assets/img/v2/icons/promotion-success.png',
            onCancel: () => {
                navigateTo('/')
            },
            onConfirm: () => {
                navigateTo('/account/general-promotions')
            },
        })
    }

    const showCancelPromotionFailedModal = (message) => {
        onShowErrorModal({
            title: t('error_modal-user-cancel_promo_failed-title'),
            message:
                message || t('error_modal-user-cancel_promo_failed-message'),
            cancelButton: t('error_modal-user-balance_failed-cancel'),
            confirmButton: t('error_modal-deny_info-cancel_button'),
            icon: '/assets/img/v2/icons/promotion-error.png',
            onCancel: () => {
                navigateTo('/')
            },
            onConfirm: () => {
                navigateTo('/account/general-promotions')
            },
        })
    }

    const showAddBankSuccessModal = () => {
        const isOnBankManagementTab = route.fullPath === '/account/bank-account'
        onShowErrorModal({
            title: t('error_modal-user-addbank_success-title'),
            message: isOnBankManagementTab
                ? t('error_modal-user-addbank_success-message_success_livechat')
                : t('error_modal-user-addbank_success-message'),
            confirmButton: isOnBankManagementTab
                ? t('error_modal-user-addbank_success-confirm_livechat')
                : t('error_modal-user-addbank_success-confirm'),
            icon: '/assets/images/modal/ic-add-bank-success.svg',
            onConfirm: () => {
                if (isOnBankManagementTab) {
                    onClickLiveChat()
                } else {
                    navigateTo('/account/bank-account')
                }
            },
        })
    }

    const showTransactionProcessingModal = (confirmAction = () => {}) => {
        onShowErrorModal({
            title: t('error_modal-transaction-processing-title'),
            message: t('error_modal-transaction-processing-message'),
            cancelButton: t('error_modal-transaction-processing-cancel'),
            confirmButton: t('error_modal-transaction-processing-confirm'),
            icon: '/assets/images/modal/ic-transaction-processing.svg',
            onConfirm: () => {
                confirmAction()
            },
        })
    }

    const showCancelTransactionWarningModal = (confirmAction = () => {}) => {
        onShowErrorModal({
            title: t('error_modal-transaction-cancel-title'),
            message: t('error_modal-transaction-cancel-message'),
            cancelButton: t('error_modal-transaction-cancel-cancel'),
            confirmButton: t('error_modal-transaction-cancel-confirm'),
            icon: '/assets/images/modal/ic-cancel-transaction-warning.svg',
            onConfirm: () => {
                confirmAction()
            },
        })
    }

    const showRequestingTransactionModal = () => {
        onShowErrorModal({
            title: t('error_modal-transaction-requesting-title'),
            message: t('error_modal-transaction-requesting-message'),
            cancelButton: t('error_modal-transaction-requesting-cancel'),
            confirmButton: t('error_modal-transaction-requesting-confirm'),
            icon: '/assets/images/modal/ic-transaction-requesting.svg',
            onConfirm: () => {
                navigateTo('/account/transaction-history')
            },
            onCancel: () => {
                navigateTo('/')
            },
        })
    }

    const showWithdrawSuccessModal = () => {
        onShowErrorModal({
            modalName: 'withdraw-success-modal',
            title: t('error_modal-withdraw-success-title'),
            message: t('error_modal-withdraw-success-message'),
            confirmButton: t('error_modal-withdraw-success-confirm'),
            icon: '/assets/img/v2/icons/withdraw-success.png',
            onClose: () => {
                navigateTo('/account/transaction-history')
            },
        })
    }

    const showWithdrawFailedModal = (message = '') => {
        onShowErrorModal({
            title: t('error_modal-withdraw-error-title'),
            message: message || t('error_modal-withdraw-error-message'),
            confirmButton: t('error_modal-withdraw-error-confirm'),
            icon: '/assets/img/v2/icons/withdraw-error.png',
            // onCancel: () => {
            //     navigateTo('/')
            // },
            // onConfirm: () => {
            //     navigateTo('/account/general-promotions')
            // },
        })
    }

    const showDepositSuccessModal = (amount = 0) => {
        onShowErrorModal({
            title: t('error_modal-deposit-success-title'),
            message: amount
                ? t('error_modal-deposit-success-message', {
                      amount,
                  })
                : t('error_modal-deposit-success-message'),
            confirmButton: t('error_modal-deposit-success-confirm'),
            cancelButton: t('error_modal-deposit-success-cancel'),
            icon: '/assets/images/modal/ic-deposit-success.svg',
            onConfirm: () => {
                navigateTo('/account/transaction-history')
            },
            onCancel: () => {
                navigateTo('/')
            },
        })
    }

    const showDepositFailedModal = (message = '') => {
        onShowErrorModal({
            title: t('error_modal-deposit-error-title'),
            message: message || t('error_modal-deposit-error-message'),
            confirmButton: t('error_modal-deposit-error-confirm'),
            cancelButton: t('error_modal-deposit-error-cancel'),
            icon: '/assets/images/modal/ic-deposit-error.svg',
            onCancel: () => {
                onClickLiveChat()
            },
        })
    }

    const showWarningOwnerBankError = () => {
        onShowErrorModal({
            type: MODAL_TYPE.ERROR,
            title: t('error_modal-system-title'),
            message: t('user-withdraw-bank-notice_1'),
            cancelButton: t('error_modal-system-confirm_cskh'),
            confirmButton: t('user-profile-profile_info-bank_add'),
            onConfirm: () => {
                showAddBankModal.value = true
            },
            onCancel: () => {
                onClickLiveChat()
            },
        })
    }

    const showOfflineWarningModal = () => {
        onShowErrorModal({
            type: MODAL_TYPE.ERROR,
            title: t('common-offline_title'),
            message: t('common-offline'),
            cancelButton: t('error_modal-system-cancel'),
            confirmButton: t('error_modal-system-confirm'),
            onCancel: () => {
                navigateTo('/')
            },
        })
    }

    return {
        showGameDenyModal,
        showGameMaintenanceModal,
        showRegisterSuccessModal,
        showRegisterFailedModal,
        showLoginFailedModal,
        showLoggedExpiredModal,
        showLogoutWarningModal,
        showSendMailSuccessModal,
        showSendMailFailedModal,
        showChangePasswordSuccessModal,
        showChangePasswordFailedModal,
        showCreatePasswordSuccessModal,
        showMessage,
        showMessageError,
        showWarningOwnerBankError,
        showMessageSuccess,
        showBalanceFailedModal,
        showCancelPromotionSuccessModal,
        showCancelPromotionFailedModal,
        showAddBankSuccessModal,
        showTransactionProcessingModal,
        showCancelTransactionWarningModal,
        showRequestingTransactionModal,
        showWithdrawSuccessModal,
        showWithdrawFailedModal,
        showDepositSuccessModal,
        showDepositFailedModal,
        showOfflineWarningModal,
        showSendTelegramSuccessModal,
        showDepositPlay,
    }
}
